import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useTaxYear } from "@/context/TaxYearContext";
import { TaxYearSelect } from "@/components/TaxYearSelect";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Plus, Trash2, Upload, FileUp, RefreshCw, AlertTriangle, ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";

const AIS_CATEGORIES = ["Salary", "Interest", "Dividend", "Rent", "TDS", "SFT", "Other"];

function Section({ title, subtitle, right, children, testid }) {
  return (
    <div className="border border-gray-200 bg-white rounded" data-testid={testid}>
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Inner() {
  const { activeId } = useCompany();
  const { ay, isComputable } = useTaxYear();
  const navigate = useNavigate();
  const [docs26AS, setDocs26AS] = useState([]);
  const [docsAIS, setDocsAIS] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy26AS, setBusy26AS] = useState(false);
  const [busyAIS, setBusyAIS] = useState(false);
  const [pushing, setPushing] = useState(false);
  const file26ASRef = useRef(null);
  const fileAISRef = useRef(null);

  const load = async () => {
    if (!activeId) return;
    try {
      const [a, b, r, s] = await Promise.all([
        api.get(`/companies/${activeId}/tax/imports/26as`),
        api.get(`/companies/${activeId}/tax/imports/ais`),
        api.get(`/companies/${activeId}/tax/imports/reconciliation?ay=${ay}`),
        api.get(`/companies/${activeId}/tax/imports/summary?ay=${ay}`),
      ]);
      setDocs26AS((a.data || []).filter((d) => d.ay === ay));
      setDocsAIS((b.data || []).filter((d) => d.ay === ay));
      setReconciliation(r.data || null);
      setSummary(s.data || null);
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  useEffect(() => { load(); }, [activeId, ay]); // eslint-disable-line react-hooks/exhaustive-deps

  const pushToComputation = async () => {
    setPushing(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/imports/push-to-computation`, { ay, target: "new" });
      toast.success("New computation created from your imports");
      navigate(`/tax/computation/${data.id}`);
    } catch (err) { toast.error(describeAxiosError(err)); }
    finally { setPushing(false); }
  };

  // ── 26AS actions ──
  const create26AS = async () => {
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/imports/26as`, {
        ay, pan: "", rows: [{ part: "A", tan: "", deductor_name: "New Deductor", section: "", amount_paid: 0, tds_deducted: 0, tds_deposited: 0 }],
      });
      toast.success("Empty 26AS created — edit below");
      await load();
      return data;
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  const upload26AS = async (fileInput, pwd) => {
    const file = fileInput._file || fileInput.target?.files?.[0];
    if (!file) return;
    setBusy26AS(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("ay", ay);
      if (pwd) fd.append("pdf_password", pwd);
      const { data } = await api.post(`/companies/${activeId}/tax/imports/26as/parse`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`AI extracted ${(data.rows || []).length} row(s) from Form 26AS`);
      await load();
    } catch (err) {
      const msg = describeAxiosError(err);
      if (!pwd && /password/i.test(msg)) {
        const p = window.prompt("This PDF is password-protected. Enter the PDF password (usually your PAN in CAPITALS + date of birth as DDMMYYYY):");
        if (p) { setBusy26AS(false); return upload26AS({ _file: file }, p); }
      }
      toast.error(msg);
    } finally {
      setBusy26AS(false);
      if (fileInput.target) fileInput.target.value = "";
    }
  };

  const delete26AS = async (id) => {
    if (!window.confirm("Delete this 26AS import?")) return;
    try {
      await api.delete(`/companies/${activeId}/tax/imports/26as/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  const update26ASRow = async (doc, idx, patch) => {
    const rows = [...(doc.rows || [])];
    rows[idx] = { ...rows[idx], ...patch };
    try {
      await api.put(`/companies/${activeId}/tax/imports/26as/${doc.id}`, {
        ay: doc.ay, pan: doc.pan, financial_year: doc.financial_year || "2025-26", rows, notes: doc.notes,
      });
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  const add26ASRow = async (doc) => {
    const rows = [...(doc.rows || []), { part: "A", tan: "", deductor_name: "", section: "", amount_paid: 0, tds_deducted: 0, tds_deposited: 0 }];
    try {
      await api.put(`/companies/${activeId}/tax/imports/26as/${doc.id}`, { ay: doc.ay, pan: doc.pan, financial_year: doc.financial_year || "2025-26", rows, notes: doc.notes });
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  const remove26ASRow = async (doc, idx) => {
    const rows = (doc.rows || []).filter((_, i) => i !== idx);
    try {
      await api.put(`/companies/${activeId}/tax/imports/26as/${doc.id}`, { ay: doc.ay, pan: doc.pan, financial_year: doc.financial_year || "2025-26", rows, notes: doc.notes });
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  // ── AIS actions ──
  const createAIS = async () => {
    try {
      await api.post(`/companies/${activeId}/tax/imports/ais`, { ay, pan: "", rows: [{ category: "Interest", information_source: "New Source", amount: 0 }] });
      toast.success("Empty AIS created — edit below");
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  const uploadAIS = async (fileInput, pwd) => {
    const file = fileInput._file || fileInput.target?.files?.[0];
    if (!file) return;
    setBusyAIS(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("ay", ay);
      if (pwd) fd.append("pdf_password", pwd);
      const { data } = await api.post(`/companies/${activeId}/tax/imports/ais/parse`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`AI extracted ${(data.rows || []).length} row(s) from AIS/TIS`);
      await load();
    } catch (err) {
      const msg = describeAxiosError(err);
      if (!pwd && /password/i.test(msg)) {
        const p = window.prompt("This PDF is password-protected. Enter the PDF password (usually your PAN in CAPITALS + date of birth as DDMMYYYY):");
        if (p) { setBusyAIS(false); return uploadAIS({ _file: file }, p); }
      }
      toast.error(msg);
    } finally {
      setBusyAIS(false);
      if (fileInput.target) fileInput.target.value = "";
    }
  };

  const deleteAIS = async (id) => {
    if (!window.confirm("Delete this AIS import?")) return;
    try {
      await api.delete(`/companies/${activeId}/tax/imports/ais/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  const updateAISRow = async (doc, idx, patch) => {
    const rows = [...(doc.rows || [])];
    rows[idx] = { ...rows[idx], ...patch };
    try {
      await api.put(`/companies/${activeId}/tax/imports/ais/${doc.id}`, { ay: doc.ay, pan: doc.pan, rows, notes: doc.notes });
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  const addAISRow = async (doc) => {
    const rows = [...(doc.rows || []), { category: "Interest", information_source: "", amount: 0 }];
    try {
      await api.put(`/companies/${activeId}/tax/imports/ais/${doc.id}`, { ay: doc.ay, pan: doc.pan, rows, notes: doc.notes });
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  const removeAISRow = async (doc, idx) => {
    const rows = (doc.rows || []).filter((_, i) => i !== idx);
    try {
      await api.put(`/companies/${activeId}/tax/imports/ais/${doc.id}`, { ay: doc.ay, pan: doc.pan, rows, notes: doc.notes });
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  return (
    <>
      <PageHeader
        title="Form 26AS · AIS / TIS Imports"
        subtitle={`AY ${ay}. Upload PDFs for AI extraction or paste rows manually — then push straight into a computation.`}
        actions={
          <>
            <TaxYearSelect />
            <Button variant="outline" onClick={() => navigate("/tax")} className="h-8" data-testid="btn-imp-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button variant="outline" onClick={load} className="h-8" data-testid="btn-imp-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </>
        }
      />
      <div className="p-6 space-y-6">
        {/* One-click push — the legit replacement for portal auto-fetch */}
        {summary && (
          <Section
            title="Push imports into a computation"
            subtitle="AIS income, 26AS/AIS TDS and advance/self-assessment challans → mapped into a new tax computation."
            testid="imports-summary-card"
          >
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex flex-wrap gap-3 flex-1">
                {[["Salary (AIS)", summary.salary_income], ["Interest (AIS)", summary.interest_income], ["Dividend (AIS)", summary.dividend_income], ["Rent (AIS)", summary.rent_income], ["TDS", summary.tds], ["Advance Tax", summary.advance_tax], ["Self-Assessment", summary.self_assessment_tax]].map(([k, v]) => (
                  <div key={k} className="px-3 py-2 rounded border border-gray-200 bg-gray-50" data-testid={`imp-sum-${k.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">{k}</div>
                    <div className="text-sm font-mono font-semibold text-gray-900">₹ {formatINR(v || 0)}</div>
                  </div>
                ))}
              </div>
              <Button onClick={pushToComputation} disabled={pushing || !isComputable} className="h-9 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 shrink-0" data-testid="btn-imports-push">
                <ArrowRightCircle className="w-4 h-4 mr-1.5" /> {pushing ? "Creating…" : "Push to new computation"}
              </Button>
            </div>
            {!isComputable && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mt-3" data-testid="imports-noncomputable-note">
                Computing a return for AY {ay} isn't supported yet — imports are stored, but the push is disabled for this year.
              </p>
            )}
          </Section>
        )}

        {/* Reconciliation Summary */}
        {reconciliation && (
          <Section
            title="Reconciliation Summary"
            subtitle={`26AS docs: ${reconciliation.documents?.count_26as || 0} · AIS docs: ${reconciliation.documents?.count_ais || 0}`}
            testid="reconciliation-card"
          >
            <table className="tx-table w-full text-xs">
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="text-right">Per 26AS / AIS</th>
                  <th className="text-right">Per Books</th>
                  <th className="text-right">Variance</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(reconciliation.rows || []).map((r) => (
                  <tr key={r.label}>
                    <td>{r.label}</td>
                    <td className="text-right font-mono">₹ {formatINR(r.ais_26as)}</td>
                    <td className="text-right font-mono">₹ {formatINR(r.books)}</td>
                    <td className={`text-right font-mono font-semibold ${Math.abs(r.variance) < 100 ? "text-emerald-700" : "text-red-700"}`} data-testid={`recon-variance-${r.label.replace(/\s+/g, "-").toLowerCase()}`}>
                      ₹ {formatINR(r.variance)}
                    </td>
                    <td>
                      {Math.abs(r.variance) >= 100 && <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* ── Form 26AS ── */}
        <Section
          title="Form 26AS — TDS / TCS Statement"
          subtitle="Downloaded from TRACES or income-tax portal."
          testid="section-26as"
          right={
            <div className="flex items-center gap-2">
              <input type="file" ref={file26ASRef} accept="application/pdf" className="hidden" onChange={upload26AS} data-testid="input-file-26as" />
              <Button variant="outline" size="sm" onClick={() => file26ASRef.current?.click()} disabled={busy26AS} className="h-8 text-xs" data-testid="btn-upload-26as">
                <Upload className="w-3.5 h-3.5 mr-1" /> {busy26AS ? "Parsing…" : "Upload PDF (AI)"}
              </Button>
              <Button size="sm" onClick={create26AS} className="h-8 text-xs bg-blue-700 hover:bg-blue-800" data-testid="btn-create-26as">
                <Plus className="w-3.5 h-3.5 mr-1" /> Manual Entry
              </Button>
            </div>
          }
        >
          {docs26AS.length === 0 ? (
            <div className="text-xs text-gray-500 py-4 text-center bg-gray-50 rounded">No 26AS imports yet. Click <em>Upload PDF (AI)</em> or <em>Manual Entry</em> to begin.</div>
          ) : docs26AS.map((doc) => (
            <div key={doc.id} className="mb-4 border border-gray-100 rounded" data-testid={`doc-26as-${doc.id}`}>
              <div className="px-3 py-2 bg-gray-50 flex items-center justify-between">
                <div className="text-xs">
                  <span className="font-mono">{doc.ay}</span>
                  <span className="mx-2 text-gray-400">·</span>
                  <span>PAN: <input value={doc.pan || ""} onChange={(e) => update26ASRow(doc, 0, {})} onBlur={(e) => api.put(`/companies/${activeId}/tax/imports/26as/${doc.id}`, { ay: doc.ay, pan: e.target.value, financial_year: doc.financial_year, rows: doc.rows, notes: doc.notes }).then(load)} className="border-b border-gray-300 bg-transparent font-mono text-xs w-32" /></span>
                  <span className="mx-2 text-gray-400">·</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${doc.source === "ai-pdf" ? "bg-purple-100 text-purple-700" : "bg-gray-200 text-gray-700"}`}>{doc.source}</span>
                  <span className="mx-2 text-gray-400">·</span>
                  <span className="font-semibold">Total TDS: ₹ {formatINR(doc.total_tds || 0)}</span>
                </div>
                <button onClick={() => delete26AS(doc.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="overflow-x-auto">
                <table className="tx-table w-full text-xs">
                  <thead>
                    <tr>
                      <th>Part</th>
                      <th>TAN</th>
                      <th>Deductor</th>
                      <th>Section</th>
                      <th className="text-right">Amount Paid</th>
                      <th className="text-right">TDS Deducted</th>
                      <th className="text-right">TDS Deposited</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(doc.rows || []).map((r, idx) => (
                      <tr key={idx}>
                        <td className="w-14"><Input value={r.part || "A"} onChange={(e) => update26ASRow(doc, idx, { part: e.target.value })} className="h-7 text-xs" /></td>
                        <td className="w-32"><Input value={r.tan || ""} onChange={(e) => update26ASRow(doc, idx, { tan: e.target.value })} className="h-7 text-xs font-mono" placeholder="TAN" /></td>
                        <td><Input value={r.deductor_name || ""} onChange={(e) => update26ASRow(doc, idx, { deductor_name: e.target.value })} className="h-7 text-xs" placeholder="Deductor" /></td>
                        <td className="w-20"><Input value={r.section || ""} onChange={(e) => update26ASRow(doc, idx, { section: e.target.value })} className="h-7 text-xs" placeholder="194J" /></td>
                        <td className="w-28"><Input type="number" value={r.amount_paid || 0} onChange={(e) => update26ASRow(doc, idx, { amount_paid: Number(e.target.value) })} className="h-7 text-right font-mono text-xs" /></td>
                        <td className="w-28"><Input type="number" value={r.tds_deducted || 0} onChange={(e) => update26ASRow(doc, idx, { tds_deducted: Number(e.target.value) })} className="h-7 text-right font-mono text-xs" /></td>
                        <td className="w-28"><Input type="number" value={r.tds_deposited || 0} onChange={(e) => update26ASRow(doc, idx, { tds_deposited: Number(e.target.value) })} className="h-7 text-right font-mono text-xs" /></td>
                        <td><button onClick={() => remove26ASRow(doc, idx)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 border-t border-gray-100">
                <Button size="sm" variant="outline" onClick={() => add26ASRow(doc)} className="h-7 text-xs"><Plus className="w-3 h-3 mr-1" /> Add row</Button>
              </div>
            </div>
          ))}
        </Section>

        {/* ── AIS / TIS ── */}
        <Section
          title="AIS / TIS — Annual Information Statement"
          subtitle="Categorized by income source (Salary / Interest / Dividend / Rent / SFT etc.)."
          testid="section-ais"
          right={
            <div className="flex items-center gap-2">
              <input type="file" ref={fileAISRef} accept="application/pdf" className="hidden" onChange={uploadAIS} data-testid="input-file-ais" />
              <Button variant="outline" size="sm" onClick={() => fileAISRef.current?.click()} disabled={busyAIS} className="h-8 text-xs" data-testid="btn-upload-ais">
                <FileUp className="w-3.5 h-3.5 mr-1" /> {busyAIS ? "Parsing…" : "Upload PDF (AI)"}
              </Button>
              <Button size="sm" onClick={createAIS} className="h-8 text-xs bg-blue-700 hover:bg-blue-800" data-testid="btn-create-ais">
                <Plus className="w-3.5 h-3.5 mr-1" /> Manual Entry
              </Button>
            </div>
          }
        >
          {docsAIS.length === 0 ? (
            <div className="text-xs text-gray-500 py-4 text-center bg-gray-50 rounded">No AIS/TIS imports yet.</div>
          ) : docsAIS.map((doc) => (
            <div key={doc.id} className="mb-4 border border-gray-100 rounded" data-testid={`doc-ais-${doc.id}`}>
              <div className="px-3 py-2 bg-gray-50 flex items-center justify-between">
                <div className="text-xs">
                  <span className="font-mono">{doc.ay}</span>
                  <span className="mx-2 text-gray-400">·</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${doc.source === "ai-pdf" ? "bg-purple-100 text-purple-700" : "bg-gray-200 text-gray-700"}`}>{doc.source}</span>
                  <span className="mx-2 text-gray-400">·</span>
                  <span>Categories: {Object.entries(doc.totals_by_category || {}).map(([k, v]) => `${k}: ₹${formatINR(v)}`).join(", ") || "—"}</span>
                </div>
                <button onClick={() => deleteAIS(doc.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="overflow-x-auto">
                <table className="tx-table w-full text-xs">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Information Source</th>
                      <th>PAN/TAN</th>
                      <th className="text-right">Amount</th>
                      <th className="text-right">TDS</th>
                      <th>Section</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(doc.rows || []).map((r, idx) => (
                      <tr key={idx}>
                        <td className="w-28">
                          <select value={r.category || "Other"} onChange={(e) => updateAISRow(doc, idx, { category: e.target.value })} className="h-7 text-xs w-full border border-gray-300 rounded px-1">
                            {AIS_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td><Input value={r.information_source || ""} onChange={(e) => updateAISRow(doc, idx, { information_source: e.target.value })} className="h-7 text-xs" placeholder="e.g. HDFC Bank" /></td>
                        <td className="w-32"><Input value={r.information_source_pan || ""} onChange={(e) => updateAISRow(doc, idx, { information_source_pan: e.target.value })} className="h-7 text-xs font-mono" /></td>
                        <td className="w-28"><Input type="number" value={r.amount || 0} onChange={(e) => updateAISRow(doc, idx, { amount: Number(e.target.value) })} className="h-7 text-right font-mono text-xs" /></td>
                        <td className="w-24"><Input type="number" value={r.tds_deducted || 0} onChange={(e) => updateAISRow(doc, idx, { tds_deducted: Number(e.target.value) })} className="h-7 text-right font-mono text-xs" /></td>
                        <td className="w-20"><Input value={r.section || ""} onChange={(e) => updateAISRow(doc, idx, { section: e.target.value })} className="h-7 text-xs" /></td>
                        <td><button onClick={() => removeAISRow(doc, idx)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 border-t border-gray-100">
                <Button size="sm" variant="outline" onClick={() => addAISRow(doc)} className="h-7 text-xs"><Plus className="w-3 h-3 mr-1" /> Add row</Button>
              </div>
            </div>
          ))}
        </Section>
      </div>
    </>
  );
}

export default function TaxImports() {
  return <RequireCompany><Inner /></RequireCompany>;
}
